import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { showPopupNew } from '../../../../actions/popup-actions';
import { PopupTypes } from '../../../../utils/constants';
import { getApiAxiosInstance } from '../../../../utils/network';
import { PowerFunnelGatedFeatures, PowerFunnelSurfaces } from '../../../Analytics/constants';
import GenericPopup from '../../../PopUps/GenericPopup';
import Img from '../../../Img';
import CollegeRow from './components/CollegeRow';
import AddCollegeModal from './components/AddCollegeModal';
import AlertCollegeInfoModal from './components/AlertCollegeInfoModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import TargetCollegeListInfoModal from './components/TargetCollegeListInfoModal';
import UpgradeToAnnualBanner from './components/UpgradeToAnnualBanner';
import styles from '../collegeProspectTab.scss';

const SUBSCRIPTION_TIER_YEAR = 'year';

const TARGET_COLLEGES_ENDPOINT = '/v1/cpp/target-colleges';
const TARGET_COLLEGES_PAGE_SIZE = 5;
const TARGET_COLLEGES_PAGE = 1;
const TARGET_COLLEGES_FULL_LIST_PATH = '/my-profile/college-prospect/target-colleges';

const formatP6 = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    return Number(value).toFixed(2);
};

const mapResponseToRow = (item) => ({
    id: item.clubId,
    name: item.name,
    p6: formatP6(item.p6),
    division: item.div,
    conference: item.conf,
    isAlerted: !!item.alertSchool,
    dateLastAlerted: item.dateLastAlerted || null,
});

const MyTargetColleges = ({ refreshKey }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth?.user);
    const isPowered = !!user?.isPowered;
    const hasAnnualPower = isPowered && user?.subscriptionTierType === SUBSCRIPTION_TIER_YEAR;

    const [colleges, setColleges] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [alertCooldownMessage, setAlertCooldownMessage] = useState(null);
    const [addCollegeError, setAddCollegeError] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteModalError, setDeleteModalError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAlertInfoOpen, setIsAlertInfoOpen] = useState(false);
    const [isListInfoOpen, setIsListInfoOpen] = useState(false);

    const fetchColleges = useCallback(async () => {
        setIsLoading(true);
        setFetchError(null);
        try {
            const params = {
                page: TARGET_COLLEGES_PAGE,
                pageSize: TARGET_COLLEGES_PAGE_SIZE,
            };
            const response = await getApiAxiosInstance().get(
                TARGET_COLLEGES_ENDPOINT,
                { params },
            );
            setColleges((response.data?.results || []).map(mapResponseToRow));
            setTotalCount(response.data?.totalCount || 0);
        } catch (err) {
            setFetchError(err?.response?.data?.DisplayMessage || 'We couldn\'t load your target colleges. Please try again.');
            setColleges([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchColleges();
    }, [fetchColleges, refreshKey]);

    const hasColleges = colleges && colleges.length > 0;
    const hasMoreThanInline = totalCount > TARGET_COLLEGES_PAGE_SIZE;

    const handleToggleAlert = async (collegeId) => {
        const target = colleges.find(college => college.id === collegeId);
        if (!target) {
            return;
        }
        const nextAlerted = !target.isAlerted;
        const previous = colleges;
        setColleges(colleges.map(college => (college.id === collegeId
            ? { ...college, isAlerted: nextAlerted }
            : college)));
        if (!nextAlerted) {
            return;
        }
        try {
            await getApiAxiosInstance().post(`${TARGET_COLLEGES_ENDPOINT}/${collegeId}/alert`);
        } catch (err) {
            setColleges(previous);
            if (err?.response?.status === 400) {
                setAlertCooldownMessage(err.response.data?.DisplayMessage || 'You cannot alert this college again yet, please try again later.');
            }
        }
    };

    const handleCloseAlertCooldownPopup = () => {
        setAlertCooldownMessage(null);
    };

    const handleRequestDelete = (collegeId) => {
        const target = colleges.find(college => college.id === collegeId);
        if (!target) {
            return;
        }
        setDeleteTarget(target);
        setDeleteModalError(null);
    };

    const handleCancelDelete = () => {
        if (isDeleting) {
            return;
        }
        setDeleteTarget(null);
        setDeleteModalError(null);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }
        setIsDeleting(true);
        setDeleteModalError(null);
        try {
            await getApiAxiosInstance().delete(`${TARGET_COLLEGES_ENDPOINT}/${deleteTarget.id}`);
            setDeleteTarget(null);
            await fetchColleges();
        } catch (err) {
            setDeleteModalError(err?.response?.data?.DisplayMessage || 'We couldn\'t remove that college. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenAlertInfo = () => {
        setIsAlertInfoOpen(true);
    };

    const handleCloseAlertInfo = () => {
        setIsAlertInfoOpen(false);
    };

    const handleOpenListInfo = () => {
        setIsListInfoOpen(true);
    };

    const handleCloseListInfo = () => {
        setIsListInfoOpen(false);
    };

    const handleAddCollegeClick = () => {
        if (hasAnnualPower) {
            setIsModalOpen(true);
            return;
        }
        const popup = isPowered
            ? PopupTypes.POWER_MONTHLY_UPGRADE_POPUP
            : PopupTypes.UPGRADE_TO_POWER_POPUP;
        dispatch(showPopupNew({
            popup,
            referringSurface: PowerFunnelSurfaces.PLAYER_PROFILE_COLLEGE_PROSPECT,
            gatedFeature: PowerFunnelGatedFeatures.COLLEGE_PROSPECT_TARGET_LIST,
        }));
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleAddCollege = async (school) => {
        if (!school?.id) {
            return;
        }
        try {
            await getApiAxiosInstance().post(TARGET_COLLEGES_ENDPOINT, {
                clubId: school.id,
            });
            setIsModalOpen(false);
            await fetchColleges();
        } catch (err) {
            setAddCollegeError(err?.response?.data?.DisplayMessage || 'We couldn\'t add that college. Please try again.');
        }
    };

    const handleCloseAddCollegeError = () => {
        setAddCollegeError(null);
    };

    return (
        <>
            <UpgradeToAnnualBanner />
            <div className={styles.collegeProspectContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <h6 className={styles.title}>My Target Colleges</h6>
                        <button
                            type="button"
                            className={styles.titleInfoBtn}
                            onClick={handleOpenListInfo}
                            aria-label="About your target college list"
                            data-testid="my-target-colleges.list-info-btn"
                        >
                            <Img
                                src="/img/v4/info-icon.svg"
                                alt=""
                                className={styles.infoIcon}
                            />
                        </button>
                    </div>
                    {hasMoreThanInline && (
                        <Link
                            to={TARGET_COLLEGES_FULL_LIST_PATH}
                            className={styles.viewAllLink}
                            data-testid="my-target-colleges.view-all-link"
                        >
                            View All
                        </Link>
                    )}
                </div>

                <div className={styles.tableContainer}>
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText}>Name</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText}>P6</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText}>Div</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText}>Con</span>
                        </div>
                        <div className={styles.tableHeaderCell}>
                            <span className={styles.tableHeaderText}>Alert College</span>
                            <button
                                type="button"
                                className={styles.alertInfoIconBtn}
                                onClick={handleOpenAlertInfo}
                                aria-label="What does Alert College do?"
                                data-testid="my-target-colleges.alert-info-btn"
                            >
                                <Img
                                    src="/img/v4/info-icon.svg"
                                    alt=""
                                    className={styles.alertInfoIcon}
                                />
                            </button>
                        </div>
                        <div className={styles.tableHeaderCell} />
                    </div>

                    <div className={styles.tableContent}>
                        {hasColleges ? (
                            <>
                                <div className={styles.collegeRowsContainer}>
                                    {colleges.map((college) => (
                                        <CollegeRow
                                            key={college.id}
                                            college={college}
                                            onToggleAlert={handleToggleAlert}
                                            onDelete={handleRequestDelete}
                                            onShowAlertInfo={handleOpenAlertInfo}
                                        />
                                    ))}
                                </div>
                                <div className={styles.addCollegeButtonWrapper}>
                                    <button
                                        type="button"
                                        className={`${styles.addCollegeButton} ${!hasAnnualPower ? styles.addCollegeButtonLocked : ''}`}
                                        onClick={handleAddCollegeClick}
                                    >
                                        <Img
                                            src="/img/v3/icons/icon-18px-circle-plus-teal.svg"
                                            alt="Add"
                                            className={styles.addIcon}
                                        />
                                        <span className={styles.addCollegeText}>Add College</span>
                                        {!hasAnnualPower && (
                                            <Img
                                                src="/img/v2/icons/icon-feature-lock.svg"
                                                alt="Locked"
                                                className={styles.addCollegeLockIcon}
                                            />
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyStateMessage}>
                                    {isLoading && 'Loading your target colleges...'}
                                    {!isLoading && fetchError && fetchError}
                                    {!isLoading && !fetchError && 'Create a list of colleges that you\'re interested in attending. Once you add colleges to your list, you will have the opportunity to alert the colleges so coaches will be able find you.'}
                                </div>
                                {!isLoading && (
                                    <div className={styles.addCollegeButtonWrapper}>
                                        <button
                                            type="button"
                                            className={`${styles.addCollegeButton} ${!hasAnnualPower ? styles.addCollegeButtonLocked : ''}`}
                                            onClick={handleAddCollegeClick}
                                        >
                                            <Img
                                                src="/img/v3/icons/icon-18px-circle-plus-teal.svg"
                                                alt="Add"
                                                className={styles.addIcon}
                                            />
                                            <span className={styles.addCollegeText}>Add College</span>
                                            {!hasAnnualPower && (
                                                <Img
                                                    src="/img/v2/icons/icon-feature-lock.svg"
                                                    alt="Locked"
                                                    className={styles.addCollegeLockIcon}
                                                />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <AddCollegeModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onAddCollege={handleAddCollege}
            />
            {alertCooldownMessage && (
                <GenericPopup
                    title="Alert Already Sent"
                    subtitle={alertCooldownMessage}
                    buttonText="Okay"
                    onButtonClick={handleCloseAlertCooldownPopup}
                    onRequestClose={handleCloseAlertCooldownPopup}
                    data-testid="modal.target-colleges.alert-cooldown"
                />
            )}
            {addCollegeError && (
                <GenericPopup
                    title="Couldn't Add College"
                    subtitle={addCollegeError}
                    buttonText="Okay"
                    onButtonClick={handleCloseAddCollegeError}
                    onRequestClose={handleCloseAddCollegeError}
                    data-testid="modal.target-colleges.add-college-error"
                />
            )}
            {deleteTarget && (
                <DeleteConfirmationModal
                    collegeName={deleteTarget.name}
                    isAlerted={deleteTarget.isAlerted}
                    errorMessage={deleteModalError}
                    isDeleting={isDeleting}
                    onCancel={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />
            )}
            {isAlertInfoOpen && (
                <AlertCollegeInfoModal onClose={handleCloseAlertInfo} />
            )}
            {isListInfoOpen && (
                <TargetCollegeListInfoModal onClose={handleCloseListInfo} />
            )}
        </>
    );
};

MyTargetColleges.defaultProps = {
    refreshKey: 0,
};

MyTargetColleges.propTypes = {
    refreshKey: PropTypes.number,
};

export default MyTargetColleges;
